import { ACCESS_AREAS, accessIsConfigured } from "@/lib/subtabAccess";

export { accessIsConfigured };

export const ACCESS_LEVELS = [
  { id: "none", label: "No Access" },
  { id: "read", label: "Read" },
  { id: "write", label: "Write" },
];

export const ACCESS_RANK = { none: 0, read: 1, write: 2 };

export const CHART_PARENT_TAB = "patients";

export const MAIN_TABS = [
  { id: "dashboard", label: "Dashboard", path: "/" },
  { id: "organizations", label: "Organizations", path: "/organizations" },
  { id: "locations", label: "Locations", path: "/locations" },
  { id: "patients", label: "Patients", path: "/patients" },
  { id: "appointments", label: "Appointments", path: "/appointments" },
  { id: "encounters", label: "Encounters", path: "/encounters" },
  { id: "icd10cm", label: "ICD-10-CM", path: "/icd10-cm" },
  { id: "cpt", label: "CPT", path: "/cpt" },
  { id: "medications", label: "Medications", path: "/medications" },
  { id: "labs", label: "Labs", path: "/labs" },
  { id: "radiology", label: "Radiology", path: "/radiology" },
  { id: "referrals", label: "Physician Referrals", path: "/physician-referrals" },
  { id: "referring-providers", label: "Referring Providers", path: "/referring-providers" },
  { id: "smartphrases", label: "SmartPhrases", path: "/smartphrases" },
  { id: "placeholder-vocabulary", label: "Placeholder Vocabulary", path: "/placeholder-vocabulary" },
  { id: "generic-report-designer", label: "Generic Report Designer", path: "/settings/generic-report-designer" },
  { id: "page-designer", label: "Page Designer", path: "/page-designer" },
  { id: "settings", label: "Settings", path: "/settings" },
];

const PATH_RULES = [
  { prefix: "/organizations", tabId: "organizations" },
  { prefix: "/locations", tabId: "locations" },
  { prefix: "/patients", tabId: "patients" },
  { prefix: "/appointments", tabId: "appointments" },
  { prefix: "/encounters", tabId: "encounters" },
  { prefix: "/icd10-cm", tabId: "icd10cm" },
  { prefix: "/cpt", tabId: "cpt" },
  { prefix: "/medications", tabId: "medications" },
  { prefix: "/labs", tabId: "labs" },
  { prefix: "/radiology", tabId: "radiology" },
  { prefix: "/physician-referrals", tabId: "referrals" },
  { prefix: "/referring-providers", tabId: "referring-providers" },
  { prefix: "/smartphrases", tabId: "smartphrases" },
  { prefix: "/placeholder-vocabulary", tabId: "placeholder-vocabulary" },
  { prefix: "/settings/generic-report-designer", tabId: "generic-report-designer" },
  { prefix: "/page-designer", tabId: "page-designer" },
  { prefix: "/settings", tabId: "settings", exact: true },
];

export function emptyMainTabMatrix(accessLevel = "none") {
  return MAIN_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    accessLevel,
  }));
}

export function setMainTabCell(matrix, tabId, accessLevel) {
  return (matrix || []).map((cell) => (cell.id === tabId ? { ...cell, accessLevel } : cell));
}

export function mainTabLevel(matrix, tabId) {
  const cell = (matrix || []).find((item) => item.id === tabId);
  return cell?.accessLevel || "none";
}

export function minAccessLevel(left, right) {
  const a = ACCESS_RANK[left] >= 0 ? left : "none";
  const b = ACCESS_RANK[right] >= 0 ? right : "none";
  return ACCESS_RANK[a] <= ACCESS_RANK[b] ? a : b;
}

export function effectiveChildLevel(parentLevel, childLevel) {
  return minAccessLevel(parentLevel, childLevel);
}

export function canAccessMainTab(maintabAccess, tabId, options = {}) {
  if (!tabId) return true;
  if (options.isAdmin) return true;
  if (!accessIsConfigured(maintabAccess)) return true;
  return mainTabLevel(maintabAccess.matrix, tabId) !== "none";
}

export function canAccessPath(maintabAccess, pathname, options = {}) {
  return canAccessMainTab(maintabAccess, mainTabFromPath(pathname), options);
}

export function effectiveMainTabLevel(maintabAccess, tabId) {
  if (!tabId) return null;
  if (!accessIsConfigured(maintabAccess)) return null;
  return mainTabLevel(maintabAccess.matrix, tabId);
}

export function canWriteMainTab(maintabAccess, tabId, options = {}) {
  if (options.isAdmin) return true;
  const level = effectiveMainTabLevel(maintabAccess, tabId);
  if (!level) return true;
  return level === "write";
}

const WRITE_ROUTE_PATTERNS = [
  { test: (pathname) => pathname === "/appointments/new" || /^\/appointments\/[^/]+\/edit$/.test(pathname), tabId: "appointments" },
  { test: (pathname) => pathname === "/smartphrases/new" || /^\/smartphrases\/[^/]+\/edit$/.test(pathname), tabId: "smartphrases" },
];

export function isMainTabWriteRoute(pathname) {
  return WRITE_ROUTE_PATTERNS.some((rule) => rule.test(pathname || ""));
}

export function writeRouteFallbackPath(pathname) {
  const tabId = mainTabFromPath(pathname);
  const tab = MAIN_TABS.find((item) => item.id === tabId);
  return tab?.path || "/";
}

export function mainTabFromPath(pathname) {
  if (!pathname || pathname === "/") return "dashboard";
  const match = PATH_RULES.find((rule) => {
    if (rule.exact) return pathname === rule.prefix;
    return pathname === rule.prefix || pathname.startsWith(`${rule.prefix}/`);
  });
  return match?.tabId || null;
}

export function filterNavByMainTabAccess(items, maintabAccess, options = {}) {
  if (options.isAdmin || !accessIsConfigured(maintabAccess)) return items;
  return items.filter((item) => {
    const tabId = mainTabFromPath(item.to);
    return canAccessMainTab(maintabAccess, tabId, options);
  });
}

export function firstAllowedMainPath(maintabAccess, options = {}) {
  if (options.isAdmin || !accessIsConfigured(maintabAccess)) return "/";
  const allowed = MAIN_TABS.find((tab) => canAccessMainTab(maintabAccess, tab.id, options));
  return allowed?.path || null;
}

export function chartSubtabGroups() {
  return ACCESS_AREAS.map((area) => ({
    id: area.id,
    label: area.label,
    subtabs: area.tabs.map((tab) => ({ id: tab.id, label: tab.label })),
  }));
}

export function accessLevelLabel(level) {
  return ACCESS_LEVELS.find((item) => item.id === level)?.label || "No Access";
}
